import { useState } from "react";

import { PinModal, useCan } from "components";
import { Element } from "components/ReactScroll/index";
import { ACL_ACTIVITIES, ACL_RESOURCES, QUESTION_RESULT_STATUSES } from "models";
import { QuestionResult } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";
import { TyreQuestionResults } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/TyreQuestionResults";
import {
  useDeleteQuestionResultPinMutation,
  usePinQuestionResultMutation
} from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/hooks";
import "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/QuestionResults.scss";
import { useAppointmentDealer, useConnectedQuestionResultIdToFilter } from "modules/AppointmentDetails/hooks";

import type { PinModelWithHistory } from "components";
import type { Appointment, Pin } from "models";
import type { DeletePinPayload } from "modules/AppointmentDetails/components/Interventions/hooks";
import type { OrderedGroupedCheck, SortedQuestionResult } from "modules/AppointmentDetails/hooks";

type QuestionResultsProps = {
  check: OrderedGroupedCheck;
  showAll?: boolean;
  appointment: Appointment;
};

export const QuestionResults = ({ check, showAll, appointment }: QuestionResultsProps) => {
  const appointmentDealer = useAppointmentDealer(appointment);
  const [selectedPinWithHistory, setSelectedPinWithHistory] = useState<PinModelWithHistory | null>(null);

  const canCreateFullPin = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinFull);

  const connectedQuestionResultId = useConnectedQuestionResultIdToFilter();
  const addPinQuestionResult = usePinQuestionResultMutation();
  const deleteQuestionResultPin = useDeleteQuestionResultPinMutation();

  const handleOpenPinModal = (pinWithHistory: PinModelWithHistory) => {
    setSelectedPinWithHistory(pinWithHistory);
  };

  const handleDeletePin = async (payload: DeletePinPayload) => {
    await deleteQuestionResultPin.mutateAsync(payload);
  };

  const handleSavePinItem = async (payload: Pin) => {
    if (!canCreateFullPin) return;
    await addPinQuestionResult.mutateAsync(payload);
  };

  const renderGroup = (groupOrder: number, questionResults: SortedQuestionResult[]) => {
    if (!questionResults.length) return;

    const visibleQuestions = questionResults.filter(questionResult => {
      if (!showAll && !questionResult.tyre && (questionResult.status === QUESTION_RESULT_STATUSES.OK || questionResult.status === QUESTION_RESULT_STATUSES.NVT)) {
        return false;
      }

      return !connectedQuestionResultId || connectedQuestionResultId === questionResult.id;
    });

    if (!visibleQuestions.length) return null;

    if (visibleQuestions.some(q => q.tyre_id))
      return (
        <Element key={"question-group-" + groupOrder} id="tyreReplacements" className="QuestionResults-group">
          <TyreQuestionResults questionResults={visibleQuestions} template={check.checklist?.template} appointment={appointment} />
        </Element>
      );

    return (
      <div key={"question-group-" + groupOrder} className="QuestionResults-group">
        <div className="QuestionResults-group_header">
          <p>{visibleQuestions[0].group_name}</p>
        </div>
        {visibleQuestions.map(questionResult => (
          <QuestionResult
            questionResult={questionResult}
            checklist={check.checklist}
            key={questionResult.id}
            appointment={appointment}
            onOpenPinModal={handleOpenPinModal}
            connectedQuestionResultId={connectedQuestionResultId}
          />
        ))}
      </div>
    );
  };

  const renderGroups = () => Array.from(check._groupedQuestionResults, ([groupOrder, questionResults]) => renderGroup(groupOrder, questionResults));

  return (
    <>
      <div className="QuestionResults">{renderGroups()}</div>
      {selectedPinWithHistory !== null && (
        <PinModal
          isOpen={true} // TODO: forced to pass true here because the pin props below does not accept null
          pin={selectedPinWithHistory}
          dealer={appointmentDealer}
          onClose={() => setSelectedPinWithHistory(null)}
          onSavePin={handleSavePinItem}
          onDeletePin={handleDeletePin}
          isSavingPin={addPinQuestionResult.isPending}
          isDeletingPin={deleteQuestionResultPin.isPending}
          check={check}
          readOnly
        />
      )}
    </>
  );
};
